const getScript = (src: string) => document.querySelector(`script[src="${src}"]`);
const getStyle = (src: string) => document.querySelector(`link[href="${src}"]`);

type CreateElement = (src: string) => HTMLScriptElement | HTMLLinkElement;

const createScript: CreateElement = src => {
  const script = document.createElement('script');
  script.src = src;
  return script;
};

const createStyle: CreateElement = src => {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = src;
  return link;
};

type AssetStatus = { status: 'success' | 'fail'; url: string };

/**
 * Load script asynchronously from `src` and return a promise that resolves
 * if the script is loaded successfully and rejects it otherwise.
 */
export const loadAssets = async (
  assets: { url: string; type: 'css' | 'js' | string; optional?: boolean }[],
  cdn = ''
): Promise<Array<AssetStatus>> => {
  return Promise.all(
    assets.map(({ url, type, optional = false }) => {
      const absoluteUrl = url.startsWith('/') ? `${cdn}${url}` : url;
      const promise =
        type === 'css' ? loadScript(absoluteUrl, getStyle, createStyle) : loadScript(absoluteUrl);

      // Successful scripts return success, optional failed scripts return fail
      if (!optional) {
        return promise.then(() => ({ status: 'success', url })) as Promise<AssetStatus>;
      }

      return promise
        .then(() => ({ status: 'success', url }))
        .catch(() => ({ status: 'fail', url })) as Promise<AssetStatus>;
    })
  );
};

let loading: { src: string; loadingPromise: Promise<unknown> }[] = [];

export function loadScript(src: string, getter = getScript, create = createScript) {
  if (loading.filter(x => x.src === src).length > 0) {
    return loading.find(x => x.src === src)!.loadingPromise;
  }
  const loadingPromise = new Promise((resolve, reject) => {
    const scriptExists = getter(src);
    if (scriptExists !== null) {
      resolve(undefined);
      return;
    }

    const script = create(src);
    script.onload = () => {
      loading = loading.filter(x => x.src !== src);
      resolve(undefined);
    };
    script.onerror = err => {
      loading = loading.filter(x => x.src !== src);
      reject(err);
    };
    document.body.appendChild(script);
  });
  loading.push({
    src,
    loadingPromise,
  });
  return loadingPromise;
}

export default loadScript;
